import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { usersAPI, rolesAPI } from '../utils/api';
import { toast } from 'sonner';
import PasswordStrengthIndicator from './PasswordStrengthIndicator';
import { ShieldCheck } from 'lucide-react';

const CreateUserDialog = ({ open, onClose }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nombre: '',
    rol_id: '',
    tipo_documento: '',
    documento_identidad: '',
  });
  
  // Security settings state
  const [securitySettings, setSecuritySettings] = useState({
    password_change_required: false,
    two_factor_enabled: false
  });
  
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);

  const tiposDocumento = [
    { value: 'DNI', label: 'DNI' },
    { value: 'RUC', label: 'RUC' },
    { value: 'Pasaporte', label: 'Pasaporte' },
    { value: 'Carnet de Extranjería', label: 'Carnet de Extranjería' },
  ];

  useEffect(() => {
    loadRoles();
  }, []);
  
  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setFormData({
        email: '',
        password: '',
        nombre: '',
        rol_id: '',
        tipo_documento: '',
        documento_identidad: '',
      });
      setSecuritySettings({
        password_change_required: false,
        two_factor_enabled: false
      });
    }
  }, [open]);

  const loadRoles = async () => {
    try {
      const response = await rolesAPI.getAll();
      setRoles(response.data);
    } catch (error) {
      toast.error('Error al cargar roles');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const submitData = {
        ...formData,
        tipo_documento: formData.tipo_documento || null,
        documento_identidad: formData.documento_identidad || null,
        two_factor_enabled: securitySettings.two_factor_enabled,
        password_change_required: securitySettings.password_change_required,
        // These are read-only with default values
        password_never_expires: false,
      };
      await usersAPI.create(submitData);
      toast.success('Usuario creado exitosamente');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#2D2D2D]" style={{ fontFamily: 'Playfair Display, serif' }}>
            Nuevo Usuario
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Nombre Completo *</Label>
            <Input
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              className="border-[#c5b8a2]"
              required
              data-testid="nombre-input"
            />
          </div>

          <div>
            <Label>Correo Electrónico *</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="border-[#c5b8a2]"
              required
              data-testid="email-input"
            />
          </div>

          <div>
            <Label>Contraseña *</Label>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="border-[#c5b8a2]"
              required
              data-testid="password-input"
            />
            
            {/* Indicador de fortaleza de contraseña */}
            {formData.password && (
              <div className="mt-3 p-3 bg-gray-50 rounded-md border border-gray-200">
                <PasswordStrengthIndicator 
                  password={formData.password} 
                  email={formData.email}
                />
              </div>
            )}
          </div>

          <div>
            <Label>Rol *</Label>
            <Select
              value={formData.rol_id}
              onValueChange={(val) => setFormData({ ...formData, rol_id: val })}
            >
              <SelectTrigger className="border-[#c5b8a2]" data-testid="rol-select">
                <SelectValue placeholder="Seleccionar rol" />
              </SelectTrigger>
              <SelectContent>
                {roles.map((rol) => (
                  <SelectItem key={rol.id} value={rol.id}>
                    {rol.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tipo de Documento</Label>
              <Select
                value={formData.tipo_documento}
                onValueChange={(val) => setFormData({ ...formData, tipo_documento: val })}
              >
                <SelectTrigger className="border-[#c5b8a2]" data-testid="tipo-documento-select">
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  {tiposDocumento.map((tipo) => (
                    <SelectItem key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Documento de Identidad</Label>
              <Input
                value={formData.documento_identidad}
                onChange={(e) => setFormData({ ...formData, documento_identidad: e.target.value.slice(0, 20) })}
                className="border-[#c5b8a2]"
                maxLength={20}
                placeholder="Número de documento"
                data-testid="documento-identidad-input"
              />
            </div>
          </div>

          {/* Security Section */}
          <div className="border-t border-[#E8E8E7] pt-4 mt-4">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck size={18} className="text-[#9d8272]" />
              <Label className="text-base font-semibold">Seguridad</Label>
            </div>
            
            {/* Password Change Required Checkbox - Editable, off por defecto */}
            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg mb-3">
              <Checkbox
                id="create_password_change_required"
                checked={securitySettings.password_change_required}
                onCheckedChange={(checked) => setSecuritySettings(prev => ({ ...prev, password_change_required: checked }))}
                data-testid="checkbox-password-change-required"
              />
              <div className="flex-1">
                <Label htmlFor="create_password_change_required" className="text-sm font-medium cursor-pointer">
                  Solicitar cambio de contraseña en el próximo inicio de sesión
                </Label>
                <p className="text-xs text-[#888888]">
                  Al iniciar sesión, el usuario será forzado a definir una nueva contraseña antes de acceder a la plataforma.
                </p>
              </div>
            </div>

            {/* 2FA Checkbox - Editable */}
            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg mb-3">
              <Checkbox
                id="create_two_factor_enabled"
                checked={securitySettings.two_factor_enabled}
                onCheckedChange={(checked) => setSecuritySettings(prev => ({ ...prev, two_factor_enabled: checked }))}
                data-testid="two-factor-checkbox"
              />
              <div className="flex-1">
                <Label htmlFor="create_two_factor_enabled" className="text-sm font-medium cursor-pointer">
                  Habilitar 2FA por correo
                </Label>
                <p className="text-xs text-[#888888]">
                  El usuario recibirá un código de verificación en su correo al iniciar sesión
                </p>
              </div>
            </div>

            {/* Password Expires Checkbox - Read Only, checked by default */}
            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg mb-3 opacity-70">
              <Checkbox
                id="create_password_expires"
                checked={true}
                disabled
                data-testid="password-expires-checkbox"
              />
              <div className="flex-1">
                <Label htmlFor="create_password_expires" className="text-sm font-medium text-[#888888]">
                  Cambio obligatorio de contraseña cada 90 días
                </Label>
                <p className="text-xs text-[#888888]">
                  Política de seguridad establecida por el sistema
                </p>
              </div>
            </div>

            {/* Password Never Expires Checkbox - Read Only, unchecked by default */}
            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg opacity-70">
              <Checkbox
                id="create_password_never_expires"
                checked={false}
                disabled
                data-testid="password-never-expires-checkbox"
              />
              <div className="flex-1">
                <Label htmlFor="create_password_never_expires" className="text-sm font-medium text-[#888888]">
                  Contraseña no caduca
                </Label>
                <p className="text-xs text-[#888888]">
                  Solo puede ser modificado por un administrador del sistema
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading || !formData.rol_id}
              className="bg-[#9d8272] hover:bg-[#8a7162] text-white"
              data-testid="submit-button"
            >
              {loading ? 'Creando...' : 'Crear Usuario'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateUserDialog;
